import type { TFunction } from "i18next";
import { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import {
  doGetaffiliationIdData,
  doGetRoleIdData,
  doLoadLogicalUnitActions,
  type U2104Affiliation,
  type U2104AvailableLogicalUnit,
  type U2104LogicalUnit,
  type U2104Organization,
  type U2104Role,
} from "../../../../U2104Service";
import { useState } from "react";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import type { AxiosInstance } from "axios";
import { useU2104LoadLogicalUnitActionHandlers } from "../../u2104Role/U2104RoleActionTab/U2104UseLoadLogicalUnitActionHandlers";
import {
  type U2104AvailableLogicalUnitChoice,
  u2104BuildAvailableLogicalUnitChoices,
} from "./u2104AvailableLogicalUnitUtils";

export type { U2104AvailableLogicalUnitChoice };

export interface U2104OrganizationAvailableLogicalUnitHandler {
  onAdd: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104AvailableLogicalUnit>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AvailableLogicalUnit>>;
  dataChangedEvent: number;
  selectableLogicalUnits: U2104AvailableLogicalUnitChoice[];
  onCheckLogicalUnit: (logicalUnit: U2104AvailableLogicalUnitChoice) => void;
  onCheckAllLogicalUnits: () => void;
  isLoadingChoices: boolean;
}

export interface U2104OrganizationAvailableLogicalUnitHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AvailableLogicalUnit>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104AvailableLogicalUnit>>;
  getOrganization: () => U2104Organization | null | undefined;
}

async function fetchReferencedLogicalUnitCodes(
  axios: AxiosInstance,
  orgId: string,
  roles: U2104Role[] | undefined,
  users: U2104Affiliation[] | undefined,
): Promise<Set<string>> {
  const codes = new Set<string>();

  const roleJobs = (roles ?? [])
    .filter((r) => typeof r.id === "string" && r.id.length > 0)
    .map(async (role) => {
      try {
        const response = await doGetRoleIdData(axios, orgId, role.id);
        const detail = response.data.result as U2104Role;
        for (const action of detail?.actions ?? []) {
          if (action.logicalUnitCode) codes.add(action.logicalUnitCode);
        }
      } catch {
        // ignore — la garde BE BU210459 reste en secours au save org
      }
    });

  const userJobs = (users ?? [])
    .filter((u) => typeof u.id === "string" && u.id.length > 0)
    .map(async (user) => {
      try {
        const response = await doGetaffiliationIdData(axios, orgId, user.id);
        const detail = response.data.result as U2104Affiliation;
        for (const action of detail?.actions ?? []) {
          if (action.logicalUnitCode) codes.add(action.logicalUnitCode);
        }
      } catch {
        // ignore
      }
    });

  await Promise.all([...roleJobs, ...userJobs]);
  return codes;
}

export function u2104OrganizationAvailableLogicalUnitHandler(
  params: U2104OrganizationAvailableLogicalUnitHandlerParam,
): U2104OrganizationAvailableLogicalUnitHandler {
  const { u2000AxiosAuthenticated, gridStateHandlers, formStateHandlers, getOrganization } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [catalogLogicalUnits, setCatalogLogicalUnits] = useState<U2104LogicalUnit[]>([]);
  const [selectableLogicalUnits, setSelectableLogicalUnits] = useState<U2104AvailableLogicalUnitChoice[]>([]);
  const [isLoadingChoices, setIsLoadingChoices] = useState<boolean>(false);

  const applyChoices = async (logicalUnits: U2104LogicalUnit[]) => {
    const org = getOrganization();
    const orgId = org?.id;
    const selectedIds = new Set(gridStateHandlers.getData().map((item) => item.logicalUnitId));
    let referencedCodes = new Set<string>();
    if (orgId) {
      setIsLoadingChoices(true);
      try {
        referencedCodes = await fetchReferencedLogicalUnitCodes(
          u2000AxiosAuthenticated,
          orgId,
          org?.roles,
          org?.users,
        );
      } finally {
        setIsLoadingChoices(false);
      }
    }
    setSelectableLogicalUnits(u2104BuildAvailableLogicalUnitChoices(logicalUnits, selectedIds, referencedCodes));
  };

  const loadCatalogHandlers = useU2104LoadLogicalUnitActionHandlers<U2104LogicalUnit>({
    doLoadLogicalUnitFn: () => doLoadLogicalUnitActions(u2000AxiosAuthenticated),
    onSuccess: (logicalUnits: U2104LogicalUnit[]) => {
      setCatalogLogicalUnits(logicalUnits);
      void applyChoices(logicalUnits);
    },
    onError: ({ error }) => {
      console.error("Error loading global logical unit catalog:", error);
      setIsLoadingChoices(false);
    },
  });

  const onCheckLogicalUnit = (logicalUnit: U2104AvailableLogicalUnitChoice): void => {
    if (logicalUnit.disabled) return;
    setSelectableLogicalUnits((prev) =>
      prev.map((lu) => (lu.id === logicalUnit.id ? { ...lu, idChecked: !lu.idChecked } : lu)),
    );
  };

  const onCheckAllLogicalUnits = (): void => {
    setSelectableLogicalUnits((prev) => {
      const toggleable = prev.filter((lu) => !lu.disabled);
      if (toggleable.length === 0) return prev;
      const allToggleableChecked = toggleable.every((lu) => lu.idChecked);
      return prev.map((lu) => (lu.disabled ? lu : { ...lu, idChecked: !allToggleableChecked }));
    });
  };

  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setErrors({});
    if (catalogLogicalUnits.length === 0) {
      setSelectableLogicalUnits([]);
      setIsLoadingChoices(true);
      loadCatalogHandlers.loadLogicalUnitActions();
    } else {
      void applyChoices(catalogLogicalUnits);
    }
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      formStateHandlers.setErrors({});
      setSelectableLogicalUnits([]);
      setDialogOpen(false);
      return;
    }

    if (result === "save") {
      const existingByLuId = new Map(
        gridStateHandlers.getData().map((row) => [row.logicalUnitId, row] as const),
      );
      // Inclure toujours les UL référencées (disabled), même si l’état local était incohérent.
      const checked = selectableLogicalUnits.filter((lu) => lu.idChecked || lu.disabled);

      const updated: U2104AvailableLogicalUnit[] = checked.map((lu) => {
        const existing = existingByLuId.get(lu.id);
        if (existing) {
          return {
            ...existing,
            logicalUnitCode: lu.logicalUnitCode,
            logicalUnitDescription: lu.logicalUnitDescription,
            idChecked: false,
          };
        }
        return {
          id: generateTemporaryId(),
          version: 0,
          logicalUnitId: lu.id,
          logicalUnitCode: lu.logicalUnitCode,
          logicalUnitDescription: lu.logicalUnitDescription,
          idChecked: false,
        };
      });

      gridStateHandlers.setData(updated);
      gridStateHandlers.setTotalRows(updated.length);
      setDataChangedEvent(dataChangedEvent + 1);
      formStateHandlers.setErrors({});
      formStateHandlers.afterSaveData(formStateHandlers.getData());
      setSelectableLogicalUnits([]);
      setDialogOpen(false);
    }
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onDelete,
    onDlgClose,
    isDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
    selectableLogicalUnits,
    onCheckLogicalUnit,
    onCheckAllLogicalUnits,
    isLoadingChoices,
  };
}
